import { distance } from '@turf/turf';
import { db } from './db.js';
import { OSM_CATEGORIES, OSM_CATEGORY_KEYS, classifyOSM, generateOverpassQuery, markerColor } from './categories.js';
import { setPOIs, getMap } from './map.js';

const RADIUS_METERS = 2000;

export { generateOverpassQuery };

function elementToPOI(element) {
  const tags = element.tags || {};
  const coordinates = element.type === 'node' ? [element.lon, element.lat] : [element.center?.lon, element.center?.lat];
  if (!Number.isFinite(coordinates[0]) || !Number.isFinite(coordinates[1])) return null;
  const osmCategory = classifyOSM(tags);
  const osmValue = tags[osmCategory] || '';
  return {
    id: `osm-${element.type}-${element.id}`,
    name: tags.name || tags.brand || osmValue || OSM_CATEGORIES[osmCategory].label,
    category: osmValue || osmCategory,
    osmCategory,
    osmValue,
    lat: Number(coordinates[1]),
    lng: Number(coordinates[0]),
    color: markerColor(osmCategory),
    tags,
    fetchedAt: Date.now(),
  };
}

export async function loadCachedPOIs(bbox = null, activeCategories = null) {
  if (!bbox) {
    setPOIs([]);
    return [];
  }
  const pois = await db.getPOIsInBounds(bbox, activeCategories);
  setPOIs(pois);
  return pois;
}

export async function fetchPOIsByBbox(bbox, activeCategories = OSM_CATEGORY_KEYS, onStatus) {
  if (!navigator.onLine) {
    onStatus?.('Offline: showing cached OSM markers.');
    return loadCachedPOIs(bbox, new Set(activeCategories));
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 30000);
  try {
    onStatus?.('Fetching real OSM markers…');
    const response = await fetch('/api/pois', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bbox, categories: activeCategories }),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`POI API HTTP ${response.status}`);
    const payload = await response.json();
    if (payload.source === 'unavailable') {
      onStatus?.('OSM source unavailable — showing cached markers.');
      return loadCachedPOIs(bbox, new Set(activeCategories));
    }
    const pois = (payload.data?.elements || []).map(elementToPOI).filter(Boolean);
    await db.upsertPOIs(pois);
    const visible = await db.getPOIsInBounds(bbox, new Set(activeCategories));
    setPOIs(visible);
    const source = payload.source === 'geofabrik-osmium' ? 'Geofabrik/Osmium' : 'Overpass';
    onStatus?.(`${pois.length} real OSM markers cached from ${source}${payload.cached ? ' + Redis' : ''}.`);
    return visible;
  } catch (error) {
    console.error('[POI] fetch failed', error);
    onStatus?.(`OSM marker fetch failed: ${error.message}`);
    return loadCachedPOIs(bbox, new Set(activeCategories));
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchNearbyPOIs(lat, lng, onStatus, activeCategories = OSM_CATEGORY_KEYS) {
  const deltaLat = RADIUS_METERS / 111320;
  const deltaLng = RADIUS_METERS / (111320 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  return fetchPOIsByBbox([lng - deltaLng, lat - deltaLat, lng + deltaLng, lat + deltaLat], activeCategories, onStatus);
}

function mapBbox(mapInstance) {
  if (!mapInstance) return null;
  // MapLibre
  if (typeof mapInstance.getBounds === 'function' && mapInstance.getBounds()?.getWest) {
    const b = mapInstance.getBounds();
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  }
  // Leaflet
  if (typeof mapInstance.getBounds === 'function') {
    const b = mapInstance.getBounds();
    if (b?.getWest) return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    if (typeof b?.getSouthWest === 'function') {
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      return [sw.lng, sw.lat, ne.lng, ne.lat];
    }
  }
  return null;
}

export function watchMapPOIs(activeCategories = OSM_CATEGORY_KEYS, onStatus) {
  const mapInstance = getMap();
  if (!mapInstance) return () => {};
  let timer = null;
  let lastKey = '';
  let inFlight = false;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      if (inFlight) return;
      const bbox = mapBbox(mapInstance);
      if (!bbox) return;
      const key = bbox.map((value) => Number(value).toFixed(2)).join(',');
      if (key === lastKey) return;
      lastKey = key;
      const categories = typeof activeCategories === 'function' ? activeCategories() : activeCategories;
      inFlight = true;
      try {
        await fetchPOIsByBbox(bbox, categories, onStatus);
      } finally {
        inFlight = false;
      }
    }, 700);
  };
  mapInstance.on('moveend', refresh);
  if (typeof mapInstance.once === 'function') mapInstance.once('load', refresh);
  // Leaflet has no 'load' event in the same way; kick immediately.
  refresh();
  return () => {
    clearTimeout(timer);
    if (typeof mapInstance.off === 'function') mapInstance.off('moveend', refresh);
  };
}

export function poiCategoryCount(pois) {
  return pois.reduce((counts, poi) => {
    counts[poi.osmCategory] = (counts[poi.osmCategory] || 0) + 1;
    return counts;
  }, {});
}

export function isNearby(poi, lng, lat) {
  return distance([lng, lat], [poi.lng, poi.lat], { units: 'meters' }) <= RADIUS_METERS;
}
